'use client';

import * as Copilot from '@copilotkit/react-core/v2';
import { type ComponentProps, useCallback, useEffect } from 'react';
import { create } from 'zustand';

export type { AssistantMessage, Message } from '@copilotkit/react-core/v2';

import { COOKIES } from '@libs/core/constants';

import { Cookie } from '../../utils/cookie';

export const useAgent: typeof Copilot.useAgent = (...args) => Copilot.useAgent(...args);
export const useAgentContext: typeof Copilot.useAgentContext = (...args) => Copilot.useAgentContext(...args);
export const useCopilotKit: typeof Copilot.useCopilotKit = (...args) => Copilot.useCopilotKit(...args);

export function CopilotKit(props: ComponentProps<typeof Copilot.CopilotKit>) {
    return <Copilot.CopilotKit {...props} />;
}

export function CopilotChatAssistantMessage(props: ComponentProps<typeof Copilot.CopilotChatAssistantMessage>) {
    return <Copilot.CopilotChatAssistantMessage {...props} />;
}

export function CopilotChatUserMessage(props: ComponentProps<typeof Copilot.CopilotChatUserMessage>) {
    return <Copilot.CopilotChatUserMessage {...props} />;
}

function CopilotChatScrollView(props: ComponentProps<typeof Copilot.CopilotChatView.ScrollView>) {
    return <Copilot.CopilotChatView.ScrollView {...props} />;
}

export const CopilotChatView = { ScrollView: CopilotChatScrollView };

interface ChatThreadStore {
    threadId?: string;
    setThreadId: (threadId: string) => void;
}

const useChatThreadStore = create<ChatThreadStore>(set => ({
    setThreadId: threadId => set({ threadId }),
}));

export function useChatThread() {
    const { threadId, setThreadId } = useChatThreadStore();

    const selectThread = useCallback(
        (nextThreadId: string) => {
            setThreadId(nextThreadId);
            Cookie.set(COOKIES.X_PETM_GHATBOT_THREAD, nextThreadId, { expires: 365 });
        },
        [setThreadId]
    );

    const resetThread = useCallback(() => {
        const newThread = crypto.randomUUID();
        selectThread(newThread);

        return newThread;
    }, [selectThread]);

    useEffect(() => {
        if (useChatThreadStore.getState().threadId) return;

        const storedThreadId = Cookie.get(COOKIES.X_PETM_GHATBOT_THREAD);
        if (storedThreadId) return setThreadId(storedThreadId);

        resetThread();
    }, [resetThread, setThreadId]);

    return { threadId, resetThread, selectThread };
}
